/**
 * Utilitaires de validation
 */

import { Exhibition, Work } from '@/shared/types';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const isValidDate = (date: Date): boolean => {
  return date instanceof Date && !isNaN(date.getTime());
};

export const isDateAfter = (date1: Date, date2: Date): boolean => {
  return date1.getTime() > date2.getTime();
};

/**
 * Valide les données d'une exposition
 */
export const validateExhibition = (
  data: Partial<Exhibition>
): ValidationResult => {
  const errors: string[] = [];

  // Vérifier que le nom en français est rempli
  if (!data.name?.fr || data.name.fr.trim().length === 0) {
    errors.push('Le titre en français est requis');
  }

  // Vérifier que le statut est valide (objet par langue)
  if (data.status) {
    Object.values(data.status).forEach((status) => {
      if (!['draft', 'published'].includes(status)) {
        errors.push('Le statut doit être "draft" ou "published"');
      }
    });
  }

  // Vérifier que l'ordre est un nombre positif si fourni
  if (data.order !== undefined && (data.order < 0 || !Number.isInteger(data.order))) {
    errors.push("L'ordre doit être un nombre entier positif");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Valide les données d'une œuvre
 */
export const validateWork = (data: Partial<Work>): ValidationResult => {
  const errors: string[] = [];

  // Vérifier que le nom en français est rempli
  if (!data.name?.fr || data.name.fr.trim().length === 0) {
    errors.push('Le titre en français est requis');
  }

  // Vérifier que le statut est valide (objet par langue)
  if (data.status) {
    Object.values(data.status).forEach((status) => {
      if (!['draft', 'published'].includes(status)) {
        errors.push('Le statut doit être "draft" ou "published"');
      }
    });
  }

  // Vérifier que l'ordre est un nombre positif si fourni
  if (data.order !== undefined && (data.order < 0 || !Number.isInteger(data.order))) {
    errors.push("L'ordre doit être un nombre entier positif");
  }

  // Vérifier qu'au moins un fichier audio est fourni
  if (data.audioUrl && Object.keys(data.audioUrl).length === 0) {
    errors.push('Au moins un fichier audio est requis');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

